import { useCallback, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { useWizard } from 'react-use-wizard';
import { Check } from 'lucide-react';

import { selectClient } from '@client/model';
import { useFeatureFlags } from '@client/contexts/FeatureFlagsContext';

import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
  Alert,
  AlertDescription,
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  Badge,
  Button,
  Card,
  CardContent,
  Separator,
} from '@design-system';
import { cn } from '@client/lib';
import { formatCurrency } from '@shared';

import { getStepIndex } from '@/features/guided-financial-planning/config';
import { useSession, useSessionMutations } from '@/features/guided-financial-planning/context';
import {
  useFinalizeSession,
  useFinalizeValidation,
  usePlanComparisonScenarios,
} from '@/features/guided-financial-planning/hooks';
import { StepLayout } from '@/features/guided-financial-planning/components/step-layout';

type PlanSelection = 'with-insurance' | 'without-insurance';

interface ComparisonCardProps {
  title: string;
  subtitle: string;
  projectedAssets: number;
  policyCoverage?: number;
  totalCoverage: number;
  coverageAdvantage?: number;
  className?: string;
  isSelected?: boolean;
  selectLabel?: string;
  selectedLabel?: string;
  onToggleSelect?: () => void;
}

function ComparisonCard({
  title,
  subtitle,
  projectedAssets,
  policyCoverage,
  totalCoverage,
  coverageAdvantage,
  className,
  isSelected = false,
  selectLabel = 'Select',
  selectedLabel = 'Selected',
  onToggleSelect,
}: ComparisonCardProps) {
  return (
    <Card
      className={cn(
        'relative p-6 transition-all',
        isSelected && 'border-primary border-2',
        className,
      )}
    >
      <div className="space-y-4">
        <div>
          <h3 className="text-lg font-semibold text-gray-900">{title}</h3>
          <p className="text-sm text-gray-600">{subtitle}</p>
        </div>

        {policyCoverage !== undefined ? (
          <div className="grid grid-cols-2 gap-4">
            <div>
              <p className="text-sm text-gray-600">Available assets</p>
              <p className="font-sans text-xl font-bold text-gray-900">
                {formatCurrency(projectedAssets)}
              </p>
            </div>
            <div>
              <p className="text-sm text-gray-600">+ Policy coverage</p>
              <p className="font-sans text-xl font-bold text-gray-900">
                {formatCurrency(policyCoverage)}
              </p>
            </div>
          </div>
        ) : (
          <div>
            <p className="text-sm text-gray-600">Available assets for care</p>
            <p className="font-sans text-xl font-bold text-gray-900">
              {formatCurrency(projectedAssets)}
            </p>
          </div>
        )}

        <Separator />

        <div>
          <p className="text-sm text-gray-600">Projected LTC Coverage</p>
          <p className="font-sans text-xl font-bold text-gray-900">
            {formatCurrency(totalCoverage)}
            {coverageAdvantage != null && coverageAdvantage > 0 && (
              <span className="ml-2 text-base font-semibold text-green-600">
                (+{formatCurrency(coverageAdvantage)})
              </span>
            )}
          </p>
        </div>

        {onToggleSelect && (
          <Button
            variant={isSelected ? 'default' : 'outline'}
            className={cn(
              'w-full',
              !isSelected && 'border-primary text-primary hover:bg-primary/10',
            )}
            onClick={onToggleSelect}
          >
            {isSelected ? (
              <>
                <Check className="mr-2 size-4" />
                {selectedLabel}
              </>
            ) : (
              selectLabel
            )}
          </Button>
        )}
      </div>
    </Card>
  );
}

interface SummaryCardProps {
  netBenefit: number;
  showCard: boolean;
}

function SummaryCard({ netBenefit, showCard }: SummaryCardProps) {
  if (!showCard) {
    return null;
  }

  return (
    <Card className="bg-primary/5 border-primary/40 p-6">
      <CardContent className="flex flex-col items-center p-0 text-center">
        <p className="mb-2 text-xs font-medium uppercase tracking-wide text-gray-600">
          Selected Plan vs Self-Funding
        </p>
        <div className="text-primary mb-1 text-3xl font-bold">+{formatCurrency(netBenefit)}</div>
        <p className="text-sm text-gray-600">more coverage than self-funding alone</p>
      </CardContent>
    </Card>
  );
}

interface FundingBreakdownCardProps {
  allocations: Array<{
    selfFundingSourceId: string;
    accountName: string;
    amount: number;
    existingValue?: number;
  }>;
  totalPolicyCost: number;
  fundingSourcesSnapshot?: MultipleFundingSources;
}

function getExistingValueFromSnapshot(
  sourceId: string,
  snapshot?: MultipleFundingSources,
): number | null {
  const source = snapshot?.selfFunding?.[sourceId];
  if (source && typeof source === 'object' && 'existingAssetsValue' in source) {
    return source.existingAssetsValue ?? null;
  }
  return null;
}

function FundingBreakdownCard({
  allocations,
  totalPolicyCost,
  fundingSourcesSnapshot,
}: FundingBreakdownCardProps) {
  if (allocations.length === 0) {
    return null;
  }

  return (
    <div className="rounded-lg border px-4">
      <Accordion
        type="single"
        collapsible
        className="w-full"
      >
        <AccordionItem
          value="funding-breakdown"
          className="border-none bg-transparent hover:bg-transparent data-[state=open]:bg-transparent"
        >
          <AccordionTrigger className="text-lg font-semibold text-gray-900 hover:no-underline">
            How your premium is funded
          </AccordionTrigger>
          <AccordionContent className="pb-4">
            <div className="space-y-3">
              {allocations.map(allocation => {
                const existingValue =
                  allocation.existingValue ??
                  getExistingValueFromSnapshot(
                    allocation.selfFundingSourceId,
                    fundingSourcesSnapshot,
                  );

                return (
                  <div
                    key={allocation.selfFundingSourceId}
                    className="flex items-center justify-between"
                  >
                    <div className="flex items-center gap-2">
                      <p className="font-medium text-gray-900">{allocation.accountName}</p>
                      {existingValue != null && existingValue > 0 && (
                        <Badge
                          variant="secondary"
                          className="font-medium"
                        >
                          {formatCurrency(existingValue)}
                        </Badge>
                      )}
                    </div>
                    <span className="font-sans font-semibold text-red-600">
                      −{formatCurrency(allocation.amount)}
                    </span>
                  </div>
                );
              })}

              {totalPolicyCost > 0 && (
                <>
                  <Separator className="my-4" />
                  <div className="flex items-center justify-between">
                    <p className="font-medium text-gray-900">Total Policy Cost</p>
                    <p className="font-sans font-semibold text-gray-900">
                      {formatCurrency(totalPolicyCost)}
                    </p>
                  </div>
                </>
              )}
            </div>
          </AccordionContent>
        </AccordionItem>
      </Accordion>
    </div>
  );
}

interface PlanComparisonContentProps {
  planComparison: ReturnType<typeof usePlanComparisonScenarios>;
  selectedPlan: PlanSelection | null;
  onTogglePlan: (plan: PlanSelection) => void;
  isReadOnly?: boolean;
  fundingSourcesSnapshot?: MultipleFundingSources;
  hasExistingLtcPolicy?: boolean;
}

function PlanComparisonContent({
  planComparison,
  selectedPlan,
  onTogglePlan,
  isReadOnly = false,
  fundingSourcesSnapshot,
  hasExistingLtcPolicy = false,
}: PlanComparisonContentProps) {
  const { flags } = useFeatureFlags();
  const {
    projectedLtcCost,
    withoutInsurance,
    withInsurance,
    selectedPolicy,
    fundingAllocations,
    netBenefit,
    benefitScenario,
  } = planComparison;

  const totalPolicyCost = selectedPolicy?.totalCost ?? 0;
  const coverageDifference = withInsurance.totalCoverage - withoutInsurance.totalCoverage;
  const insuranceHasMoreCoverage = coverageDifference > 0;
  const selfFundingHasMoreCoverage = coverageDifference < 0;

  return (
    <div className="mx-auto max-w-4xl space-y-6">
      <SummaryCard
        netBenefit={netBenefit}
        showCard={benefitScenario.insuranceIsBetter}
      />

      <div className="grid gap-6 md:grid-cols-2">
        <ComparisonCard
          title={hasExistingLtcPolicy ? 'Current coverage' : 'Without Insurance'}
          subtitle={hasExistingLtcPolicy ? 'Self-funding + existing policy' : 'Self-funding only'}
          projectedAssets={withoutInsurance.projectedAssets}
          totalCoverage={withoutInsurance.totalCoverage}
          coverageAdvantage={selfFundingHasMoreCoverage ? Math.abs(coverageDifference) : undefined}
          isSelected={selectedPlan === 'without-insurance'}
          selectLabel="Continue with self-funding"
          selectedLabel="Selected"
          onToggleSelect={isReadOnly ? undefined : () => onTogglePlan('without-insurance')}
        />
        <ComparisonCard
          title="With Insurance"
          subtitle={
            flags.nycLifeDemo ? 'Selected Policy' : selectedPolicy?.productName || 'Selected Policy'
          }
          projectedAssets={withInsurance.projectedAssets}
          policyCoverage={
            withInsurance.policyCoverage > 0 ? withInsurance.policyCoverage : undefined
          }
          totalCoverage={withInsurance.totalCoverage}
          coverageAdvantage={insuranceHasMoreCoverage ? coverageDifference : undefined}
          isSelected={selectedPlan === 'with-insurance'}
          selectLabel="Continue with insurance"
          selectedLabel="Selected"
          onToggleSelect={isReadOnly ? undefined : () => onTogglePlan('with-insurance')}
        />
      </div>

      <FundingBreakdownCard
        allocations={fundingAllocations}
        totalPolicyCost={totalPolicyCost}
        fundingSourcesSnapshot={fundingSourcesSnapshot}
      />
    </div>
  );
}

export function PlanComparisonStep() {
  const navigate = useNavigate();
  const client = useSelector(selectClient);
  const { flags } = useFeatureFlags();
  const { goToStep } = useWizard();
  const { session, isLoading: isSessionLoading } = useSession();
  const { isSaving, abandonSession, updateStepData } = useSessionMutations();
  const { finalizeSession, isFinalizing } = useFinalizeSession();
  const { validate, errors: validationErrors } = useFinalizeValidation();
  const planComparison = usePlanComparisonScenarios();

  const selectedPlan = useMemo((): PlanSelection | null => {
    // Don't apply defaults until session is loaded
    if (isSessionLoading) {
      return null;
    }
    const saved = session?.stepData?.selectedPlanType;
    if (saved === 'with-insurance' || saved === 'without-insurance') {
      return saved;
    }
    // Only default to 'with-insurance' if selectedPlanType was never set (undefined)
    // If it's explicitly null (user deselected), respect that
    if (saved === null) {
      return null;
    }
    return 'with-insurance';
  }, [session?.stepData?.selectedPlanType, isSessionLoading]);

  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [showWithoutInsuranceDialog, setShowWithoutInsuranceDialog] = useState(false);
  const [showValidationErrors, setShowValidationErrors] = useState(false);
  const [isAbandoning, setIsAbandoning] = useState(false);

  const isSessionComplete = session?.status === 'completed';

  const handleAdjustFunding = useCallback(() => {
    goToStep(getStepIndex('quotes-funding'));
  }, [goToStep]);

  const handleTogglePlan = useCallback(
    (plan: PlanSelection) => {
      const newSelection = selectedPlan === plan ? null : plan;
      setShowValidationErrors(false);
      updateStepData({ selectedPlanType: newSelection });
    },
    [selectedPlan, updateStepData],
  );

  const handleFinalize = useCallback(async () => {
    await finalizeSession(planComparison);
    if (client.clientId) {
      navigate(`/clients/${client.clientId}/onboarding/funding-sources`);
    }
  }, [finalizeSession, planComparison, navigate, client.clientId]);

  const handleAbandonSession = useCallback(async () => {
    if (!client.clientId) return;

    setIsAbandoning(true);
    try {
      await abandonSession();
      navigate(`/clients/${client.clientId}/onboarding/funding-sources`);
    } finally {
      setIsAbandoning(false);
    }
  }, [abandonSession, client.clientId, navigate]);

  const handleNextClick = useCallback(() => {
    if (isSessionComplete) {
      if (client.clientId) {
        navigate(`/clients/${client.clientId}/onboarding/funding-sources`);
      }
      return;
    }

    if (!selectedPlan) {
      return;
    }

    if (selectedPlan === 'without-insurance') {
      setShowWithoutInsuranceDialog(true);
      return;
    }

    const result = validate();
    if (!result.isValid) {
      setShowValidationErrors(true);
      return;
    }

    setShowValidationErrors(false);
    setShowConfirmDialog(true);
  }, [isSessionComplete, client.clientId, navigate, selectedPlan, validate]);

  const handleConfirmFinalize = useCallback(async () => {
    setShowConfirmDialog(false);
    await handleFinalize();
  }, [handleFinalize]);

  const handleConfirmWithoutInsurance = useCallback(async () => {
    setShowWithoutInsuranceDialog(false);
    await handleAbandonSession();
  }, [handleAbandonSession]);

  const isWithoutInsurance = selectedPlan === 'without-insurance';
  const nextButtonLabel = isSessionComplete
    ? 'Done'
    : isWithoutInsurance
      ? 'Continue Without Insurance'
      : 'Finalize My Plan';
  const nextButtonIcon =
    isSessionComplete || isWithoutInsurance ? undefined : <Check className="size-5" />;
  const nextButtonVariant = isWithoutInsurance ? 'ghost' : 'default';
  const isNextDisabled = !isSessionComplete && !selectedPlan;

  return (
    <>
      <StepLayout
        title="Your Plan Comparison"
        subtitle=""
        backButtonLabel="Adjust Funding"
        nextButtonLabel={nextButtonLabel}
        nextButtonIcon={nextButtonIcon}
        nextButtonVariant={nextButtonVariant}
        onBack={handleAdjustFunding}
        onNext={handleNextClick}
        isLoading={isSaving || isFinalizing || isAbandoning}
        isNextDisabled={isNextDisabled}
      >
        <PlanComparisonContent
          planComparison={planComparison}
          selectedPlan={selectedPlan}
          onTogglePlan={handleTogglePlan}
          isReadOnly={isSessionComplete}
          fundingSourcesSnapshot={session?.stepData?.multipleFundingSourcesSnapshot}
          hasExistingLtcPolicy={
            flags.existingLtcPolicyTypeOnCard &&
            (session?.stepData?.selectedAssets ?? []).includes('existing-ltc-policy')
          }
        />
        {showValidationErrors && validationErrors.length > 0 && (
          <Alert
            variant="destructive"
            className="mx-auto mt-4 max-w-4xl"
          >
            <AlertDescription>
              <p className="text-sm font-medium ">
                Could not finalize your plan. Please review the following errors and try again:
              </p>
              <ul className="list-disc pl-4">
                {validationErrors.map(error => (
                  <li key={error.code}>{error.message}</li>
                ))}
              </ul>
            </AlertDescription>
          </Alert>
        )}
      </StepLayout>

      <AlertDialog
        open={showConfirmDialog}
        onOpenChange={setShowConfirmDialog}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Ready to finalize?</AlertDialogTitle>
            <AlertDialogDescription>
              This will save a snapshot of your current plan. It will not be editable after
              finalization.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Keep Editing</AlertDialogCancel>
            <AlertDialogAction onClick={handleConfirmFinalize}>Finalize</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog
        open={showWithoutInsuranceDialog}
        onOpenChange={setShowWithoutInsuranceDialog}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Continue without insurance?</AlertDialogTitle>
            <AlertDialogDescription>
              You've chosen to rely on your existing savings and assets to cover future long-term
              care costs. You can always explore insurance options later if you change your mind.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Go Back</AlertDialogCancel>
            <AlertDialogAction onClick={handleConfirmWithoutInsurance}>
              Continue Without Insurance
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
